import axios from 'axios';

// In production, API is served from same origin (/api)
// In development, proxy to localhost:5000
const baseURL = process.env.REACT_APP_API_URL || (
    process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api'
);

const api = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 60000, // 60s – large IMAP mailboxes can be slow
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Track if a refresh is already in progress to avoid parallel refresh calls
let isRefreshing = false;
let refreshQueue = []; // [{resolve, reject}] waiting for the new token

function processRefreshQueue(newToken, error) {
    refreshQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(newToken));
    refreshQueue = [];
}

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;
        const data = error.response?.data;
        const originalRequest = error.config;

        // ---- Token expired: try silent refresh ----
        if (
            status === 401 &&
            (data?.expired || data?.message === 'Token Expired') &&
            !originalRequest._retried
        ) {
            // If impersonating, fall back to admin token
            const adminToken = localStorage.getItem('adminToken');
            const adminUser = localStorage.getItem('adminUser');
            if (adminToken && adminUser) {
                localStorage.setItem('token', adminToken);
                localStorage.setItem('user', adminUser);
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                localStorage.removeItem('impersonating');
                window.location.reload();
                return new Promise(() => {});
            }

            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
                originalRequest._retried = true;

                if (isRefreshing) {
                    // Queue this request until refresh resolves
                    return new Promise((resolve, reject) => {
                        refreshQueue.push({ resolve, reject });
                    }).then((newToken) => {
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return api(originalRequest);
                    });
                }

                isRefreshing = true;
                try {
                    const res = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
                    const newToken = res.data.token;
                    const newUser = res.data.user;
                    localStorage.setItem('token', newToken);
                    if (newUser) localStorage.setItem('user', JSON.stringify(newUser));
                    processRefreshQueue(newToken, null);
                    isRefreshing = false;
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    return api(originalRequest);
                } catch (refreshErr) {
                    processRefreshQueue(null, refreshErr);
                    isRefreshing = false;
                    // Refresh failed – clear everything and redirect
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('refreshToken');
                    if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                        window.location.href = '/login?expired=1';
                        return new Promise(() => {});
                    }
                    return Promise.reject(refreshErr);
                }
            }

            // No refresh token – redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                window.location.href = '/login?expired=1';
                return new Promise(() => {});
            }
        }

        if (status === 401 && data?.message === 'Invalid Token') {
            const adminToken = localStorage.getItem('adminToken');
            const adminUser = localStorage.getItem('adminUser');
            if (adminToken && adminUser) {
                localStorage.setItem('token', adminToken);
                localStorage.setItem('user', adminUser);
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                localStorage.removeItem('impersonating');
                window.location.reload();
                return new Promise(() => {});
            }
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('refreshToken');
            if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                window.location.href = '/login?expired=1';
                return new Promise(() => {});
            }
        }

        // ---- 402 from verifyModule = "trial exhausted / upgrade required".
        // Broadcast a custom DOM event so the active page can react
        // (open an upsell dialog, refresh module state, etc.) without making
        // every component duplicate the same try/catch.
        if (status === 402 && data?.upgradeRequired && !originalRequest?.suppressModuleUpsell) {
            try {
                window.dispatchEvent(new CustomEvent('module:upgrade-required', {
                    detail: {
                        moduleName: data.moduleName,
                        accessLevel: data.accessLevel,
                        message: data.message,
                        trialAutomationCap: data.trialAutomationCap,
                        trialRuleCap: data.trialRuleCap,
                    },
                }));
            } catch (e) { /* ignore */ }
        }

        if (status === 403 && data?.message === 'Invalid Token') {
            const adminToken = localStorage.getItem('adminToken');
            const adminUser = localStorage.getItem('adminUser');
            if (adminToken && adminUser) {
                localStorage.setItem('token', adminToken);
                localStorage.setItem('user', adminUser);
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                localStorage.removeItem('impersonating');
                window.location.reload();
                return new Promise(() => {});
            }
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('refreshToken');
            if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                window.location.href = '/login?expired=1';
                return new Promise(() => {});
            }
        }

        return Promise.reject(error);
    }
);

export default api;
